import OpticalGymBaseClass from "./opticalgym_base_class";
import {
    AnaglyphAppConfig,
    AnaglyphClinicPrefs,
    AnaglyphOrientation,
    defaultAnaglyphOrientation,
} from "../lib/anaglyph_orientation";
import {
    calibrationFingerprint,
    currentCalibrationDeviceIdentity,
    defaultColourCorrection,
    guessCalibrationMeasurements,
    resolveStoredCalibration,
} from "../lib/calibration_core";

export { AnaglyphOrientation };

export interface ColourCalibration {
  red_brightness: number;
  blue_brightness: number;
  green_brightness: number;
}

const CALIBRATION_STORAGE_KEY = 'opticalgym_calibration';

// Local storage can throw outright rather than return nothing (a browser
// configured to block site data), and the calibration for a screen can be
// answered from the user's server-side records alone, so every access to the
// local cache is best-effort.
function readLocalCalibration(): string | undefined {
    try {
        return window.localStorage[CALIBRATION_STORAGE_KEY];
    } catch {
        return undefined;
    }
}

function writeLocalCalibration(serialized_calibration: string) {
    try {
        window.localStorage[CALIBRATION_STORAGE_KEY] = serialized_calibration;
    } catch {
        // Nothing to do: the server records remain the source of truth.
    }
}

export class Calibration {
    id: string | null = null;
    user_id: string | null = null;
    width_px: number;
    height_px: number;
    pixel_ratio: number;
    height_cm: number;
    distance_cm: number;
    anaglyph_orientation: AnaglyphOrientation;
    colour_correction: ColourCalibration;
    fingerprint: string;
    no_touch_screen: boolean | null;

    constructor(
        clinic_prefs: AnaglyphClinicPrefs | null = null,
        app_config: AnaglyphAppConfig | null =
            typeof window !== "undefined" ? window.app_config : null,
    ) {
        const guess = guessCalibrationMeasurements();
        this.id = '';
        this.width_px = guess.width_px;
        this.height_px = guess.height_px;
        this.pixel_ratio = guess.pixel_ratio;
        this.height_cm = guess.height_cm;
        this.distance_cm = guess.distance_cm;
        this.anaglyph_orientation = defaultAnaglyphOrientation(clinic_prefs, app_config);
        this.colour_correction = defaultColourCorrection();
        this.fingerprint = guess.fingerprint;
        this.no_touch_screen = null;
    }

    // Load the calibration from local storage, if it exists and fingerprint matches, otherwise return null.
    // This runs while the store is being constructed, so a device that cannot
    // be read for is reported as uncalibrated rather than as a failure to start.
    static load(): Calibration | null {
        const calibration = resolveStoredCalibration([], readLocalCalibration());
        return calibration ? Calibration.fromJson(calibration) : null;
    }

    static clearLocal() {
        try {
            window.localStorage.removeItem(CALIBRATION_STORAGE_KEY);
        } catch {
            // Nothing to do: there is no cache to clear.
        }
    }

    setDefaultValues(
        clinic_prefs: AnaglyphClinicPrefs | null = null,
        app_config: AnaglyphAppConfig | null =
            typeof window !== "undefined" ? window.app_config : null,
    ) {
        const guess = guessCalibrationMeasurements();
        this.id = '';
        this.width_px = guess.width_px;
        this.height_px = guess.height_px;
        this.pixel_ratio = guess.pixel_ratio;
        this.height_cm = guess.height_cm;
        this.distance_cm = guess.distance_cm;
        this.anaglyph_orientation = defaultAnaglyphOrientation(clinic_prefs, app_config);
        this.colour_correction = defaultColourCorrection();
        this.fingerprint = guess.fingerprint;
        this.no_touch_screen = null;
    }

    refreshDeviceIdentity() {
        Object.assign(this, currentCalibrationDeviceIdentity());
    }

    // Store to local storage
    storeToLocal() {
        writeLocalCalibration(JSON.stringify(this));
    }

    // Calculate the screen fingerprint
    static calcFingerprint(): string {
        return calibrationFingerprint();
    }

    static fromJson(data: Object): Calibration {
        let calibration = new Calibration();
        Object.assign(calibration, data);
        calibration.fingerprint =
            typeof (data as { fingerprint?: unknown }).fingerprint === "string"
                ? (data as { fingerprint: string }).fingerprint
                : "";
        return calibration;
    }

    static fromPreviousForCurrentDevice(previous: Calibration): Calibration {
        const calibration = new Calibration();
        calibration.id = null;
        calibration.user_id = null;
        calibration.height_cm = previous.height_cm;
        calibration.distance_cm = previous.distance_cm;
        calibration.anaglyph_orientation = previous.anaglyph_orientation;
        calibration.colour_correction = { ...previous.colour_correction };
        calibration.no_touch_screen = previous.no_touch_screen;
        calibration.refreshDeviceIdentity();
        return calibration;
    }
}

export class UserCalibrationMetadata extends OpticalGymBaseClass {
    id: string | null = null;
    data: Calibration | null = null;
    user_id: string | null = null;
    clinic_id: string | null = null;
    category: string = 'calibration';

    static fromJson(data: { [key: string]: any }): UserCalibrationMetadata {
        let calibration_metadata = new UserCalibrationMetadata();
        Object.assign(calibration_metadata, data);
        calibration_metadata.data = Calibration.fromJson(data['data']);
        calibration_metadata.data.id = data['id'] || null;
        calibration_metadata.data.user_id = data['user_id'] || null;
        return calibration_metadata;
    }
}

export class CalibrationList {
    data: Calibration[];

    constructor() {
        this.data = [];
    }

    public getMostRecentCalibration(): null | Calibration {
        return this.data[0] || null;
    }

    public addMostRecentCalibration(calibration: Calibration) {
        this.data.unshift(calibration);
    }

    public getCurrentCalibration(): null | Calibration {
        const resolved_calibration = resolveStoredCalibration(
            this.data,
            readLocalCalibration(),
        );
        const calibration = resolved_calibration
            ? resolved_calibration instanceof Calibration
                ? resolved_calibration
                : Calibration.fromJson(resolved_calibration)
            : null;

        // Set local-storage cache
        if (calibration) {
            calibration.storeToLocal();
        }

        return calibration;
    }

    // Preserves the order the API returned, which `user_metadata_for_user()`
    // sorts by `updated` descending. `getCurrentCalibration()` takes the first
    // record that matches this device, so that is the most recently saved one.
    static fromResponse(response_data: Array<UserCalibrationMetadata>) {
        let calibration_list = new CalibrationList();

        for (var i in response_data) {
            let metadata = response_data[i];
            let calibration_data = metadata.data!;
            calibration_data.id = metadata.id;
            calibration_data.user_id = metadata.user_id;

            let calibration = Calibration.fromJson(calibration_data);
            calibration_list.data.push(calibration);
        }

        return calibration_list;
    }
}
